import { KNOWN_CATEGORIES } from './categories.ts';
import type {
  GroundTruthPageEntry,
  Submission,
  SubmissionPage,
} from './types.ts';

export type GradeOutcome =
  | 'exact'
  | 'sub_category_mismatch'
  | 'discipline_mismatch'
  | 'skipped'
  | 'no_prediction'
  | 'no_truth';

export interface PageGrade {
  page_index: number;
  predicted_category: string | null;
  truth_category: string | null;
  predicted_discipline: string | null;
  truth_discipline: string | null;
  score: 0 | 0.5 | 1;
  outcome: GradeOutcome;
}

export interface JobGrade {
  submissionId: string;
  scorePct: number;
  pageGrades: readonly PageGrade[];
  totalGraded: number;
  totalSkipped: number;
  totalExact: number;
  totalSubMismatch: number;
  totalDisciplineMismatch: number;
  totalNoPrediction: number;
  totalNoTruth: number;
}

const KNOWN_VALUE_TO_GROUP = new Map(KNOWN_CATEGORIES.map((c) => [c.value, c.group] as const));

// Returns the discipline bucket for a category value.
//   - Known disciplines map to their own value ('structural', 'civil', ...)
//   - Known specials map to their own value (each special is its own bucket;
//     special-vs-special = sub_category_mismatch, special-vs-discipline =
//     discipline_mismatch).
//   - Free-text "Other" categories map to '__other__' so they cluster in the
//     confusion matrix (Phase 4) instead of fragmenting the report.
//   - Splits on '/' if present (forward-compat for sub-categories like
//     'structural/footing').
//   - Returns null for null input.
export function disciplineOf(category: string | null): string | null {
  if (category === null) return null;
  const head = category.includes('/') ? category.split('/')[0] : category;
  if (KNOWN_VALUE_TO_GROUP.has(head)) return head;
  return '__other__';
}

export function gradePage(predicted: SubmissionPage, truth: GroundTruthPageEntry): PageGrade {
  const predictedCategory = predicted.category;
  const truthCategory = truth.category;
  const predictedDiscipline = disciplineOf(predictedCategory);
  const truthDiscipline = disciplineOf(truthCategory);

  const base = {
    page_index: truth.page_index,
    predicted_category: predictedCategory,
    truth_category: truthCategory,
    predicted_discipline: predictedDiscipline,
    truth_discipline: truthDiscipline,
  };

  if (truth.source === 'skipped') {
    return { ...base, score: 0, outcome: 'skipped' };
  }
  if (truthCategory === null) {
    return { ...base, score: 0, outcome: 'no_truth' };
  }
  if (predictedCategory === null) {
    return { ...base, score: 0, outcome: 'no_prediction' };
  }
  if (predictedCategory === truthCategory) {
    return { ...base, score: 1, outcome: 'exact' };
  }
  if (predictedDiscipline !== null && predictedDiscipline === truthDiscipline) {
    // Same discipline, different sub-category. Until sub-categories ship,
    // this branch is unreachable (category === discipline === sub for known
    // values). Code is here so Inc 8 sub-cats activate scoring without
    // edits.
    return { ...base, score: 0.5, outcome: 'sub_category_mismatch' };
  }
  return { ...base, score: 0, outcome: 'discipline_mismatch' };
}

// Returns null when the submission is not promoted to corpus or its
// ground_truth snapshot is missing — the caller should render the
// "promote to enable grading" hint instead.
export function gradeJob(
  submission: Submission,
  pages: readonly SubmissionPage[],
): JobGrade | null {
  if (submission.kind !== 'corpus') return null;
  const groundTruth = submission.ground_truth;
  if (!groundTruth || !Array.isArray(groundTruth.pages)) return null;

  const pageByIndex = new Map<number, SubmissionPage>();
  for (const p of pages) pageByIndex.set(p.page_index, p);

  const pageGrades: PageGrade[] = [];
  let totalGraded = 0;
  let totalSkipped = 0;
  let totalExact = 0;
  let totalSubMismatch = 0;
  let totalDisciplineMismatch = 0;
  let totalNoPrediction = 0;
  let totalNoTruth = 0;
  let scoreSum = 0;

  for (const truth of groundTruth.pages) {
    const predicted = pageByIndex.get(truth.page_index);
    // If a page that was in the snapshot has since been deleted, model it as
    // no_prediction so it still surfaces in the grade.
    const predictedRow: SubmissionPage = predicted ?? {
      id: `missing-${truth.page_index}`,
      submission_id: submission.id,
      page_index: truth.page_index,
      page_label: truth.page_label,
      category: null,
      category_type: null,
      confidence: null,
      status: null,
      review_decision: null,
      review_human_category: null,
      vision_applied: false,
      vision_reasoning: null,
    };

    const grade = gradePage(predictedRow, truth);
    pageGrades.push(grade);

    switch (grade.outcome) {
      case 'exact':
        totalGraded += 1;
        totalExact += 1;
        scoreSum += grade.score;
        break;
      case 'sub_category_mismatch':
        totalGraded += 1;
        totalSubMismatch += 1;
        scoreSum += grade.score;
        break;
      case 'discipline_mismatch':
        totalGraded += 1;
        totalDisciplineMismatch += 1;
        break;
      case 'no_prediction':
        totalGraded += 1;
        totalNoPrediction += 1;
        break;
      case 'no_truth':
        totalNoTruth += 1;
        break;
      case 'skipped':
        totalSkipped += 1;
        break;
    }
  }

  const scorePct = totalGraded === 0 ? 0 : Math.round((scoreSum / totalGraded) * 1000) / 10;

  return {
    submissionId: submission.id,
    scorePct,
    pageGrades,
    totalGraded,
    totalSkipped,
    totalExact,
    totalSubMismatch,
    totalDisciplineMismatch,
    totalNoPrediction,
    totalNoTruth,
  };
}
